import React from 'react'
import Big from 'big.js'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { BEIGE_600, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import type { PfVatResolution } from 'dataQueries/purchaseFlowRevampVat.query'
import type { PaymentMethod } from 'models/purchaseFlowRevamp'
import type { MonetaryValueV2 } from 'models/purchaseFlowRevamp/catalogueV2DTO'
import { formatPercentage, formatPrice } from 'utils/price/priceFormatter'
import { SystemMessage } from 'components/common/SystemMessage/SystemMessage.component'

import type { OneSystemCartSummary } from '../../store/oneSystemPricing'
import { useChargeParityTripwire } from './useChargeParityTripwire'

interface Props {
  total: Big
  currency: string | null
  /**
   * Org-wide catalogue discount as a PERCENTAGE (`8.5` = 8.5%), the same
   * convention as `organizations.discount` and the v1 `FeeDTO`.
   * `null`/`undefined`/`0` all render as "no discount".
   */
  discountPercentage?: number | null
  /**
   * The method the order will be paid by, when one is known. Only consulted
   * while no VAT rate is known — see the `vat` prop.
   */
  paymentMethod?: PaymentMethod
  /**
   * The VAT the order will actually be billed, once an invoicing address has
   * been chosen and the backend has resolved a rate against it. Absent on every
   * step before Billing, where the cart falls back to stating what will happen
   * to VAT rather than how much of it there is.
   */
  vat?: PfVatResolution | null
  /**
   * The OneSystem cart block: the contract banner once a pack is covered, and the public
   * price of the charged lines with the partner discount taken off it — legacy embed parity.
   */
  oneSystem?: OneSystemCartSummary
  /**
   * What the Stripe intent will charge (gross, major units), when one is open and settled. Never
   * rendered: it only feeds the tripwire that reports a gross differing from the charge.
   */
  chargedAmount?: MonetaryValueV2 | null
}

/**
 * Subtotal / VAT (placeholder) / total block at the bottom of the cart.
 *
 * The Subtotal row is hidden when it equals Total (i.e. when
 * no VAT or discount adjustment has been applied). For v1 the two values are
 * always equal (VAT is deferred to the invoice), so the Subtotal row is
 * unconditionally hidden — showing identical numbers under different labels
 * was visually misleading.
 *
 * VAT label changed from "Calculated at checkout" to
 * "VAT applied on invoice" — the original copy was contradictory since the
 * user is already at the checkout / review step.
 *
 * The VAT row shows the AMOUNT once one is knowable, and the block
 * then matches legacy `/purchase-flow` line for line: a net Total, a
 * `VAT (X%)` row, and an `After VAT` gross row. The rate is not in the
 * catalogue (every v2 price is explicitly ex-VAT); it is resolved from the
 * chosen invoicing address by `usePfVatRate`, because VAT is a property of who
 * is billed, not of what is bought — an AT client with a verified VAT number is
 * reverse-charged at 0% on the same cart a DE client pays 19% on.
 *
 * Until an address is chosen there is no honest figure, so the row keeps
 * the wording, which still says the truest available thing: an invoice
 * order is billed VAT on the invoice, a card or SEPA order is charged gross at
 * checkout. That wording is what a client paying by card needed after reading a
 * total of 190,32 EUR and being charged 227,56 EUR (order 10515614) — it is a
 * weaker statement than an amount, not a wrong one, and it is all the cart can
 * say on the catalogue step.
 *
 * The net and the gross are rounded half up to the cent, because that is what
 * the charge does: `ConcreteFee.asSmallestUnit` rounds the discounted gross half
 * up (ISSUE-4066; it truncated before). Only those two are rounded — the VAT and
 * discount rows are DERIVED from them. Rounding each row on its own left the
 * column not adding up: on the cart ISSUE-3364 was filed for, Total 410,84 +
 * VAT 78,06 sat above an After-VAT row of 488,89. Deriving the rows is the one
 * arrangement where they reconcile and the gross equals the charge. Legacy
 * follows the same rule (#183); the two flows must not quote different numbers
 * for one cart.
 *
 * P5: when a non-null, non-zero `discountPercentage` is present, SX-F14's
 * premise no longer holds — Subtotal (gross `total`) and the displayed Total
 * (discounted) genuinely differ, so a `Subtotal` row reappears above a
 * `Discount −X%` row, and the Total row applies the factor via `Big` (never
 * float math). Absent/zero discount renders EXACTLY what rendered before.
 */
/** Decimal places a currency is actually charged in. */
const CENTS = 2

export const CartSummary: React.FC<Props> = ({ total, currency, discountPercentage, paymentMethod, vat, oneSystem, chargedAmount }) => {
  const { t } = useTranslation(['purchase_flow_revamp', 'order'])

  // Clamped: a value outside 0-100 is bad data, and letting it through renders a
  // negative total, which reads as a charge the client cannot make sense of.
  const percentage = Math.min(Math.max(discountPercentage ?? 0, 0), 100)
  const hasDiscount = percentage > 0

  const discountedExact = hasDiscount
    ? total.times(Big(100).minus(percentage).div(100))
    : total
  const discountedTotal = discountedExact.round(CENTS, Big.roundHalfUp)
  const discountAmount = hasDiscount ? total.minus(discountedTotal) : new Big(0)

  // Card and SEPA both settle through Stripe at checkout, VAT included. Only
  // consulted while no rate is known — an amount always beats a description.
  const isChargedAtCheckout = paymentMethod === 'card' || paymentMethod === 'sepa'

  // A negative rate is bad data; clamping to 0 keeps the gross >= the net
  // rather than rendering a total below what the client agreed to.
  const vatRate = vat ? Math.max(vat.vatRate, 0) : null
  // VAT is charged on the discounted amount, as legacy does — the discount is a
  // price reduction, not a post-tax rebate.
  // Rounded from the EXACT discounted net, so the gross is what the backend
  // computes before it rounds — deriving it from the already-rounded net would
  // shift the VAT base by up to half a cent.
  const grossTotal = vatRate === null
    ? null
    : discountedExact.times(Big(100).plus(vatRate).div(100)).round(CENTS, Big.roundHalfUp)
  const vatAmount = grossTotal === null ? null : grossTotal.minus(discountedTotal)
  useChargeParityTripwire(grossTotal, currency, chargedAmount)

  const formatAmount = (amount: Big) => formatPrice(amount, currency)

  const subtotalLabel = formatAmount(total)
  const showPartnerDiscount = !!oneSystem?.isOneSystemCart && oneSystem.partnerDiscount.gt(0)
  // The contract rate when there is nothing charged to derive it from, as in legacy.
  const partnerDiscountPercentLabel = oneSystem && oneSystem.subtotal.gt(0)
    ? formatPercentage(oneSystem.partnerDiscount.div(oneSystem.subtotal).times(100))
    : formatPercentage(8.5)
  const totalLabel = formatAmount(discountedTotal)
  const discountAmountLabel = `−${formatAmount(discountAmount)}`
  // One formatter for both percentages, shared with legacy (#183): it drops trailing
  // zeros ("10", not "10.0" or "10.00"), keeps a real decimal ("8,5"), and renders in
  // the viewer's locale — a hand-rolled `toString()` puts a dot in front of a German
  // client. Legacy moved off "19.00" in the same commit, so matching it keeps one cart
  // from labelling the same rate two ways.
  const discountPercentLabel = hasDiscount ? formatPercentage(percentage) : ''
  const vatPercentLabel = vatRate === null ? '' : formatPercentage(vatRate)
  const vatAmountLabel = vatAmount === null ? '' : formatAmount(vatAmount)
  const grossTotalLabel = grossTotal === null ? '' : formatAmount(grossTotal)

  return (
    <Stack
      gap={0.6}
      sx={{
        borderTop: `1px solid ${BEIGE_600}`,
        pt: 1.2,
      }}
    >

      {oneSystem?.hasCoveredLine &&
        <SystemMessage variant="success" message={t('order:cart.one_system_included_banner')} />
      }

      {showPartnerDiscount && oneSystem &&
        <>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="text-xs" color={GRAY_700}>
              {t('order:cart.sub_total')}
            </Typography>
            <Typography variant="text-xs" color={GRAY_700}>
              {formatAmount(oneSystem.subtotal)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="text-xs" color={GRAY_700}>
              {t('order:cart.partner_discount', { percentage: partnerDiscountPercentLabel })}
            </Typography>
            <Typography variant="text-xs" color={GRAY_700}>
              {`−${formatAmount(oneSystem.partnerDiscount)}`}
            </Typography>
          </Stack>
        </>
      }

      {/* The partner block already opens the column with a subtotal; a second one reads as a different sum. */}
      {hasDiscount && !showPartnerDiscount &&
        <Stack direction="row" justifyContent="space-between">

          <Typography variant="text-xs" color={GRAY_700}>
            {t('purchase_flow_revamp:cart.subtotal_label')}
          </Typography>

          <Typography variant="text-xs" color={GRAY_700}>
            {subtotalLabel}
          </Typography>

        </Stack>
      }

      {hasDiscount &&
        <Stack direction="row" justifyContent="space-between">

          <Typography variant="text-xs" color={GRAY_700}>
            {t('purchase_flow_revamp:cart.discount_label', { percentage: discountPercentLabel })}
          </Typography>

          <Typography variant="text-xs" color={GRAY_700}>
            {discountAmountLabel}
          </Typography>

        </Stack>
      }

      {vatRate === null &&
        <Stack direction="row" justifyContent="space-between">

          <Typography variant="text-xs" color={GRAY_700}>
            {t('purchase_flow_revamp:cart.vat_label')}
          </Typography>

          <Typography variant="text-xs" color={GRAY_700}>
            {isChargedAtCheckout
              ? t('purchase_flow_revamp:cart.vat_at_payment')
              : t('purchase_flow_revamp:cart.vat_pending')}
          </Typography>

        </Stack>
      }

      <Stack direction="row" justifyContent="space-between" sx={{ pt: 0.4 }}>

        <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>
          {t('purchase_flow_revamp:cart.total_label')}
        </Typography>

        <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>
          {totalLabel}
        </Typography>

      </Stack>

      {vatRate !== null &&
        <Stack direction="row" justifyContent="space-between" sx={{ pt: 0.4 }}>

          <Typography variant="text-xs" color={GRAY_700}>
            {t('purchase_flow_revamp:cart.vat_rate_label', { percentage: vatPercentLabel })}
          </Typography>

          <Typography variant="text-xs" color={GRAY_700}>
            {vatAmountLabel}
          </Typography>

        </Stack>
      }

      {/* A bare "VAT (0.00%)" is accurate and says nothing about why. */}
      {vat?.isReverseCharged &&
        <Typography variant="text-xs" color={GRAY_700}>
          {t('purchase_flow_revamp:cart.vat_reverse_charge')}
        </Typography>
      }

      {vatRate !== null &&
        <Stack direction="row" justifyContent="space-between">

          <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>
            {t('purchase_flow_revamp:cart.after_vat_label')}
          </Typography>

          <Typography variant="text-sm" fontWeight={600} color={GRAY_900}>
            {grossTotalLabel}
          </Typography>

        </Stack>
      }

    </Stack>
  )
}
